import { z } from 'zod';
import { uuidSchema } from './common.schema.js';
import { PaginationQuerySchema, PaginationMetaSchema } from './pagination.schema';

// ─── Row schema (full DB row) ─────────────────────────────────────────────────

export const PatientDonorPreferenceRowSchema = z.object({
  preference_id:  uuidSchema,
  hospital_id:    uuidSchema,
  patient_id:     uuidSchema,
  encounter_id:   uuidSchema.nullable().optional(),
  donor_id:       uuidSchema,
  priority_order: z.number().int(),
  reason:         z.string().nullable().optional(),
  is_active:      z.boolean(),
  set_by:         z.string().min(1).max(200).optional(),
  effective_from: z.coerce.date(),
  effective_to:   z.coerce.date().nullable().optional(),
  created_at:     z.coerce.date(),
  updated_at:     z.coerce.date(),
});
export type PatientDonorPreferenceRow = z.infer<typeof PatientDonorPreferenceRowSchema>;

// ─── Query params ─────────────────────────────────────────────────────────────

export const PatientsByDonorQuerySchema = PaginationQuerySchema.extend({
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .describe('Filter by active status (default: active only)'),
});
export type PatientsByDonorQuery = z.infer<typeof PatientsByDonorQuerySchema>;

// ─── Single item in the list response ────────────────────────────────────────

export const PatientsByDonorItemSchema = z.object({
  // ── preference fields ────────────────────────────────────────────────────
  preference_id:  uuidSchema,
  priority_order: z.number().int(),
  reason:         z.string().nullable().optional(),
  is_active:      z.boolean(),
  effective_from: z.coerce.date(),
  effective_to:   z.coerce.date().nullable().optional(),

  // ── mother-infant link ───────────────────────────────────────────────────
  link_id: uuidSchema.nullable().optional(),

  // ── patient fields ───────────────────────────────────────────────────────
  patient_id: uuidSchema,
  name:       z.string(),
  mrn:        z.string(),
  dob:        z.coerce.date(),
  sex:        z.string().nullable().optional(),

  // ── current location ─────────────────────────────────────────────────────
  unit:     z.string().nullable().optional(),
  bed:      z.string().nullable().optional(),
  location: z.string().nullable().optional(),

  // ── allergies ─────────────────────────────────────────────────────────────
  allergies: z.array(z.object({
    allergen_id: uuidSchema,
    name:        z.string(),
    code:        z.string(),
    severity:    z.string().nullable().optional(),
    status:      z.string(),
  })),
});
export type PatientsByDonorItem = z.infer<typeof PatientsByDonorItemSchema>;

// ─── List response ─────────────────────────────────────────────────────────

export const GetPatientsByDonorResponseSchema = z.object({
  items:      z.array(PatientsByDonorItemSchema),
  pagination: PaginationMetaSchema,
});
export type GetPatientsByDonorResponse = z.infer<typeof GetPatientsByDonorResponseSchema>;

// ─── Donors by Patient ─────────────────────────────────────────────────────

export const DonorsByPatientQuerySchema = PaginationQuerySchema.extend({
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .describe('Filter by active status (default: active only)'),
});
export type DonorsByPatientQuery = z.infer<typeof DonorsByPatientQuerySchema>;

export const DonorsByPatientItemSchema = z.object({
  // ── preference fields ────────────────────────────────────────────────────
  preference_id:  uuidSchema,
  priority_order: z.number().int(),
  reason:         z.string().nullable().optional(),
  is_active:      z.boolean(),
  effective_from: z.coerce.date(),
  effective_to:   z.coerce.date().nullable().optional(),

  // ── mother-infant link ───────────────────────────────────────────────────
  link_id:           uuidSchema.nullable().optional(),
  relationship_type: z.string().nullable().optional(),

  // ── donor (milk_provider) fields ─────────────────────────────────────────
  person_id:    uuidSchema,
  person_type:  z.string(),
  name:         z.string(),
  donor_code:   z.string().nullable().optional(),
  mrn:          z.string().nullable().optional(),
  dob:          z.coerce.date().nullable().optional(),
  blood_type:   z.string().nullable().optional(),
  phone:        z.string().nullable().optional(),
  email:        z.string().nullable().optional(),
  screening_status: z.string(),
  /** The donor PERSON's own discharge status (milk_providers.is_active) -- distinct from the
   *  preference/link `is_active` above, which only reflects whether this link is active. */
  donor_is_active: z.boolean(),

  // ── location ─────────────────────────────────────────────────────────────
  bed: z.object({
    bed_id:     uuidSchema,
    bed_number: z.string(),
    bed_type:   z.string(),
    bed_status: z.string(),
  }).nullable().optional(),
  unit: z.object({
    care_unit_id: uuidSchema,
    unit_code:    z.string(),
    unit_name:    z.string(),
    unit_type:    z.string(),
  }).nullable().optional(),
  facility: z.object({
    facility_id:   uuidSchema,
    facility_code: z.string(),
    facility_name: z.string(),
    facility_type: z.string(),
  }).nullable().optional(),
});
export type DonorsByPatientItem = z.infer<typeof DonorsByPatientItemSchema>;

export const GetDonorsByPatientResponseSchema = z.object({
  items:      z.array(DonorsByPatientItemSchema),
  pagination: PaginationMetaSchema,
});
export type GetDonorsByPatientResponse = z.infer<typeof GetDonorsByPatientResponseSchema>;
